import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Tooltip,
  ListSubheader,
  CircularProgress,
  Box,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/vi";

dayjs.locale("vi");

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AddEditInvoiceTemplateModal = React.memo(
  ({
    open,
    onClose,
    onSave,
    loading,
    currentTemplateToEdit,
    setCurrentTemplateToEdit,
    isReplace,
    formErrors,
    // Các mảng dữ liệu cho dropdown
    decrees,
    invoiceTypes,
    signPrefixs,
    templates,
    industries,
  }) => {
    const [itSign, setItSign] = useState("");
    const [itCQTFull, setItCQTFull] = useState("");
    const [previewLogo, setPreviewLogo] = useState("");
    const [imageLogoFile, setImageLogoFile] = useState(null); // Lưu file ảnh để gửi lên server

    /* GIỮ NGUYÊN LOGIC: Tạo lại ký hiệu và mã CQT khi có thay đổi */
    useEffect(() => {
      if (currentTemplateToEdit) {
        let ItSignPrefix = currentTemplateToEdit.ItSignPrefix;
        if (ItSignPrefix === "" || ItSignPrefix === null)
          ItSignPrefix = signPrefixs[0]?.ItSignPrefix;
        let ItInvoiceType = currentTemplateToEdit.ItInvoiceType;
        if (ItInvoiceType === "" || ItInvoiceType === null)
          ItInvoiceType = invoiceTypes[0]?.ItInvoiceType;
        let ItSignSuffix = currentTemplateToEdit.ItSignSuffix;
        let year = currentTemplateToEdit.ItCreateDate
          ? dayjs(currentTemplateToEdit.ItCreateDate).format("YY")
          : dayjs().format("YY");
        setItSign(`${ItSignPrefix}${year}${ItInvoiceType}${ItSignSuffix}`);

        let ItCQT = currentTemplateToEdit.ItCQT || "";
        let ItTemplate = currentTemplateToEdit.ItTemplate || "";
        setItCQTFull(
          `${ItInvoiceType}${ItTemplate}-${year}-${ItCQT}-***********`,
        );
      }
    }, [
      currentTemplateToEdit?.ItSignPrefix,
      currentTemplateToEdit?.ItInvoiceType,
      currentTemplateToEdit?.ItSignSuffix,
      currentTemplateToEdit?.ItCreateDate,
      currentTemplateToEdit?.ItCQT,
      currentTemplateToEdit?.ItTemplate,
      signPrefixs,
      invoiceTypes,
    ]);
    const handleFileChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        // if (file.size > 2 * 1024 * 1024) {
        //   // Giới hạn 2MB
        //   toast.error("Kích thước ảnh quá lớn (tối đa 2MB)");
        //   return;
        // }
        setCurrentTemplateToEdit({
          ...currentTemplateToEdit,
          invoiceTemplateLogoOld: currentTemplateToEdit.invoiceTemplateLogo, // Lưu lại logo cũ để có thể xóa trên server nếu cần
        });
        setPreviewLogo(URL.createObjectURL(file));
        setImageLogoFile(file);
      }
    };

    const handleRemoveLogo = () => {
      setCurrentTemplateToEdit((prev) => ({
        ...prev,
        invoiceTemplateLogoOld: prev.invoiceTemplateLogo, // Lưu lại logo cũ để có thể xóa trên server nếu cần
        invoiceTemplateLogo: "", // Xóa logo trên giao diện
      }));
      setPreviewLogo("");

      // Reset input file nếu cần để có thể chọn lại cùng một file vừa xóa
      const fileInput = document.getElementById("upload-logo-button");
      if (fileInput) fileInput.value = "";
    };

    useEffect(() => {
      if (open && currentTemplateToEdit?.invoiceTemplateLogo && !isReplace) {
        setPreviewLogo(
          API_BASE_URL + (currentTemplateToEdit?.invoiceTemplateLogo || ""),
        );
      } else {
        setPreviewLogo("");
        setCurrentTemplateToEdit((prev) => ({
          ...prev,
          invoiceTemplateLogo: "",
        }));
      }
      setImageLogoFile(null);
    }, [currentTemplateToEdit?.invoiceTemplateLogo, open]);

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
          <DialogTitle>
            {currentTemplateToEdit?.ItId
              ? isReplace
                ? "Thay thế ký hiệu"
                : "Sửa ký hiệu"
              : "Thêm ký hiệu mới"}
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Grid container spacing={2} direction="row">
              <Grid size={{ xs: 4 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Logo thương hiệu
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    height: 120,
                    border: "1px dashed #ccc",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    mb: 1,
                    backgroundColor: "#f9f9f9",
                    position: "relative", // Quan trọng để đặt nút Xóa đè lên
                    "&:hover .delete-overlay": { opacity: 1 }, // Hiệu ứng hiện nút khi hover
                  }}
                >
                  {previewLogo ? (
                    <>
                      <img
                        src={previewLogo}
                        alt="Logo Preview"
                        style={{
                          maxHeight: "100%",
                          maxWidth: "100%",
                          objectFit: "contain",
                        }}
                      />
                      <Tooltip title="Xóa ảnh">
                        <IconButton
                          size="small"
                          onClick={handleRemoveLogo}
                          sx={{
                            position: "absolute",
                            top: 5,
                            right: 5,
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            color: "error.main",
                            "&:hover": {
                              backgroundColor: "#fff",
                              color: "error.dark",
                            },
                            boxShadow: 1,
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      Chưa có ảnh
                    </Typography>
                  )}
                </Box>

                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="upload-logo-button"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="upload-logo-button">
                  <Button
                    variant="outlined"
                    component="span"
                    size="small"
                    startIcon={<AddIcon />}
                  >
                    Upload Ảnh
                  </Button>
                </label>

                <Divider sx={{ my: 2 }} />

                {/* Khu vực phân bổ 5 trường mới ở cột bên trái */}
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: "bold", mt: 3.3, fontSize: 16 }}
                >
                  Cấu hình dữ liệu & Làm tròn
                </Typography>

                <Box>
                  <DateTimePicker
                    label="Đồng bộ hóa đơn từ ngày"
                    format=""
                    value={
                      currentTemplateToEdit?.invoiceTemplateSyncFrom
                        ? dayjs(currentTemplateToEdit.invoiceTemplateSyncFrom)
                        : null
                    }
                    onChange={(newValue) => {
                      setCurrentTemplateToEdit((prev) => ({
                        ...prev,
                        invoiceTemplateSyncFrom: newValue,
                      }));
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        margin: "dense",
                      },
                    }}
                  />
                  <DateTimePicker
                    label="Lấy thông tin hóa đơn từ ngày"
                    value={
                      currentTemplateToEdit?.lastTimeGetInfor
                        ? dayjs(currentTemplateToEdit.lastTimeGetInfor)
                        : null
                    }
                    onChange={(newValue) => {
                      setCurrentTemplateToEdit((prev) => ({
                        ...prev,
                        lastTimeGetInfor: newValue,
                      }));
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        margin: "dense",
                      },
                    }}
                  />
                </Box>

                <Grid container columnSpacing={1}>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      margin="dense"
                      type="number"
                      label="Làm tròn số lượng"
                      size="small"
                      fullWidth
                      value={currentTemplateToEdit?.roundQuantity ?? 0}
                      onChange={(e) => {
                        let val = parseInt(e.target.value, 10);
                        if (isNaN(val)) val = 0;
                        if (val < 0) val = 0;
                        if (val > 5) val = 5;
                        setCurrentTemplateToEdit((prev) => ({
                          ...prev,
                          roundQuantity: val,
                        }));
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      margin="dense"
                      type="number"
                      label="Làm tròn đơn giá"
                      size="small"
                      fullWidth
                      value={currentTemplateToEdit?.roundUnitPrice ?? 0}
                      onChange={(e) => {
                        let val = parseInt(e.target.value, 10);
                        if (isNaN(val)) val = 0;
                        if (val < 0) val = 0;
                        if (val > 5) val = 5;
                        setCurrentTemplateToEdit((prev) => ({
                          ...prev,
                          roundUnitPrice: val,
                        }));
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      margin="dense"
                      type="number"
                      label="Làm tròn thành tiền"
                      size="small"
                      fullWidth
                      value={currentTemplateToEdit?.roundAmount ?? 0}
                      onChange={(e) => {
                        let val = parseInt(e.target.value, 10);
                        if (isNaN(val)) val = 0;
                        if (val < 0) val = 0;
                        if (val > 5) val = 5;
                        setCurrentTemplateToEdit((prev) => ({
                          ...prev,
                          roundAmount: val,
                        }));
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      margin="dense"
                      type="number"
                      label="Làm tròn tỷ lệ (%)"
                      size="small"
                      fullWidth
                      value={currentTemplateToEdit?.roundPercent ?? 0}
                      onChange={(e) => {
                        let val = parseInt(e.target.value, 10);
                        if (isNaN(val)) val = 0;
                        if (val < 0) val = 0;
                        if (val > 5) val = 5;
                        setCurrentTemplateToEdit((prev) => ({
                          ...prev,
                          roundPercent: val,
                        }));
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 8 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    margin="dense"
                    label="Tên địa điểm/Máy tính tiền"
                    fullWidth
                    variant="outlined"
                    size="small"
                    required
                    value={currentTemplateToEdit?.ItLocation || ""}
                    onChange={(e) =>
                      setCurrentTemplateToEdit((prev) => ({
                        ...prev,
                        ItLocation: e.target.value,
                      }))
                    }
                    error={!!formErrors.itLocation}
                    helperText={formErrors.itLocation}
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    margin="dense"
                    label="Hotline"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={currentTemplateToEdit?.invoiceTemplatePhone || ""}
                    onChange={(e) =>
                      setCurrentTemplateToEdit((prev) => ({
                        ...prev,
                        invoiceTemplatePhone: e.target.value,
                      }))
                    }
                    error={!!formErrors.invoiceTemplatePhone}
                    helperText={formErrors.invoiceTemplatePhone}
                    sx={{ flex: 1 }}
                  />
                </Box>
                <TextField
                  margin="dense"
                  label="Địa chỉ"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={currentTemplateToEdit?.invoiceTemplateAddress || ""}
                  onChange={(e) =>
                    setCurrentTemplateToEdit((prev) => ({
                      ...prev,
                      invoiceTemplateAddress: e.target.value,
                    }))
                  }
                  error={!!formErrors.invoiceTemplateAddress}
                  helperText={formErrors.invoiceTemplateAddress}
                />
                <TextField
                  margin="dense"
                  select
                  label="Nghị định"
                  fullWidth
                  size="small"
                  required
                  value={
                    currentTemplateToEdit?.ItDecree ||
                    (decrees.length > 0 && decrees[0].ItDecree) ||
                    ""
                  }
                  onChange={(e) =>
                    setCurrentTemplateToEdit((prev) => ({
                      ...prev,
                      ItDecree: e.target.value,
                    }))
                  }
                  error={!!formErrors.itDecree}
                  helperText={formErrors.itDecree}
                >
                  {decrees.map((option) => (
                    <MenuItem key={option.ItDecree} value={option.ItDecree}>
                      {option.ItDecree}
                    </MenuItem>
                  ))}
                </TextField>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    margin="dense"
                    select
                    label="Ký hiệu tiền tố"
                    size="small"
                    required
                    sx={{ flex: 2 }}
                    value={
                      currentTemplateToEdit?.ItSignPrefix ||
                      signPrefixs[0]?.ItSignPrefix ||
                      ""
                    }
                    onChange={(e) =>
                      setCurrentTemplateToEdit((prev) => ({
                        ...prev,
                        ItSignPrefix: e.target.value,
                      }))
                    }
                    error={!!formErrors.itSignPrefix}
                    helperText={formErrors.itSignPrefix}
                  >
                    {signPrefixs.map((option) => (
                      <MenuItem
                        key={option.ItSignPrefix}
                        value={option.ItSignPrefix}
                      >
                        {option.ItSignPrefixName}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    margin="dense"
                    select
                    label="Loại hoá đơn"
                    size="small"
                    required
                    sx={{ flex: 2 }}
                    value={
                      currentTemplateToEdit?.ItInvoiceType ||
                      invoiceTypes[0]?.ItInvoiceType ||
                      ""
                    }
                    onChange={(e) =>
                      setCurrentTemplateToEdit((prev) => ({
                        ...prev,
                        ItInvoiceType: e.target.value,
                      }))
                    }
                    error={!!formErrors.itInvoiceType}
                    helperText={formErrors.itInvoiceType}
                  >
                    {invoiceTypes.map((option) => (
                      <MenuItem
                        key={option.ItInvoiceType}
                        value={option.ItInvoiceType}
                      >
                        {option.ItInvoiceTypeName}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    margin="dense"
                    label="Ký hiệu hậu tố"
                    size="small"
                    required
                    sx={{ flex: 1 }}
                    inputProps={{ maxLength: 2 }}
                    value={currentTemplateToEdit?.ItSignSuffix || ""}
                    onChange={(e) =>
                      setCurrentTemplateToEdit((prev) => ({
                        ...prev,
                        ItSignSuffix: e.target.value.toUpperCase(),
                      }))
                    }
                    error={!!formErrors.itSignSuffix}
                    helperText={formErrors.itSignSuffix}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    margin="dense"
                    select
                    label="Mẫu hoá đơn"
                    size="small"
                    required
                    sx={{ flex: 1 }}
                    value={
                      currentTemplateToEdit?.ItTemplate ||
                      (templates.length > 0 && templates[0].ItTemplate) ||
                      ""
                    }
                    onChange={(e) =>
                      setCurrentTemplateToEdit((prev) => ({
                        ...prev,
                        ItTemplate: e.target.value,
                      }))
                    }
                    error={!!formErrors.itTemplate}
                    helperText={formErrors.itTemplate}
                  >
                    {templates.map((option) => (
                      <MenuItem
                        key={option.ItTemplate}
                        value={option.ItTemplate}
                      >
                        {option.ItTemplateName}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    margin="dense"
                    label="Ký hiệu hóa đơn"
                    size="small"
                    sx={{ flex: 1 }}
                    value={itSign || ""}
                    slotProps={{
                      input: { readOnly: true },
                      inputLabel: { shrink: true },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Cấp bởi cơ quan thuế" arrow>
                    <TextField
                      margin="dense"
                      label="Mã CQT"
                      size="small"
                      required
                      sx={{ flex: 1 }}
                      inputProps={{ maxLength: 5 }}
                      value={currentTemplateToEdit?.ItCQT || ""}
                      onChange={(e) =>
                        setCurrentTemplateToEdit((prev) => ({
                          ...prev,
                          ItCQT: e.target.value.toUpperCase(),
                        }))
                      }
                      error={!!formErrors.itCQT}
                      helperText={formErrors.itCQT}
                    />
                  </Tooltip>

                  <TextField
                    margin="dense"
                    label="Mã CQT sẽ cấp"
                    size="small"
                    sx={{ flex: 1 }}
                    value={itCQTFull || ""}
                    slotProps={{
                      input: { readOnly: true },
                      inputLabel: { shrink: true },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    margin="dense"
                    type="number"
                    label="Số bắt đầu CQT"
                    size="small"
                    sx={{ flex: 1 }}
                    value={
                      currentTemplateToEdit?.invoiceTemplateBeginNumCqt || ""
                    }
                    onChange={(e) =>
                      setCurrentTemplateToEdit((prev) => ({
                        ...prev,
                        invoiceTemplateBeginNumCqt: e.target.value,
                      }))
                    }
                    error={!!formErrors.invoiceTemplateBeginNumCqt}
                    helperText={formErrors.invoiceTemplateBeginNumCqt}
                  />

                  <TextField
                    margin="dense"
                    select
                    label="Ngành nghề"
                    size="small"
                    sx={{ flex: 2 }}
                    value={currentTemplateToEdit?.industryId || ""}
                    onChange={(e) =>
                      setCurrentTemplateToEdit((prev) => ({
                        ...prev,
                        industryId: e.target.value,
                      }))
                    }
                    error={!!formErrors.industryId}
                    helperText={formErrors.industryId}
                    required
                  >
                    {[
                      ...new Set(industries.map((i) => i.industryGroupName)),
                    ].map((group) => [
                      <ListSubheader
                        key={group}
                        sx={{ fontWeight: "bold", color: "black" }}
                      >
                        {group}
                      </ListSubheader>,
                      industries
                        .filter((item) => item.industryGroupName === group)
                        .map((option) => (
                          <MenuItem
                            key={option.industryId}
                            value={option.industryId}
                            sx={{ pl: 4 }}
                          >
                            {currentTemplateToEdit?.customerBusinessTypeId === 3
                              ? option.industryName
                              : option.industryNameUi}
                          </MenuItem>
                        )),
                    ])}
                  </TextField>
                </Box>
                <TextField
                  margin="dense"
                  label="Ghi chú phần header (nếu có)"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={currentTemplateToEdit?.invoiceTemplateHeaderNote || ""}
                  onChange={(e) =>
                    setCurrentTemplateToEdit((prev) => ({
                      ...prev,
                      invoiceTemplateHeaderNote: e.target.value,
                    }))
                  }
                />
                <TextField
                  margin="dense"
                  label="Ghi chú phần footer (nếu có)"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={currentTemplateToEdit?.invoiceTemplateFooterNote || ""}
                  onChange={(e) =>
                    setCurrentTemplateToEdit((prev) => ({
                      ...prev,
                      invoiceTemplateFooterNote: e.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="primary">
              Hủy
            </Button>
            <Button
              onClick={() => onSave(imageLogoFile)}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Lưu"}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    );
  },
);

export default AddEditInvoiceTemplateModal;
